'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { getYValueForBezier } from '@/utils';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import { DEMO_COLORS_DARK } from '@/constants/Wham.colors';

import Space from '@/components/Space';
import ToggleButtonGroup from '@/components/SleekToggleButtonGroup';

type TimingMode = 'linear' | 'ease-in' | 'steps';

const NUM_POINTS = 10;
const GRAPH_WIDTH = 100;
const GRAPH_HEIGHT = 150;

const STEPS_COUNT = 5;

const STEP_SEGMENT_WIDTH = GRAPH_WIDTH / STEPS_COUNT;

function progressLinear(t: number): number {
  return t;
}

function progressEaseIn(t: number): number {
  return getYValueForBezier(t, 0.42, 0, 1, 1);
}

const stepY = (level: number) =>
  (level / (STEPS_COUNT - 1)) * GRAPH_HEIGHT;

function getPoints(mode: TimingMode): Array<[number, number]> {
  if (mode === 'steps') {
    const points: Array<[number, number]> = [];
    for (let i = 0; i <= STEPS_COUNT; i++) {
      const x = i * STEP_SEGMENT_WIDTH;
      if (i === 0) {
        points.push([x, stepY(0)]);
      } else {
        points.push([x, stepY(i - 1)]);
        if (i < STEPS_COUNT) {
          points.push([x, stepY(i)]);
        }
      }
    }
    return points;
  }
  return Array.from({ length: NUM_POINTS }, (_, i) => {
    const t = i / (NUM_POINTS - 1);
    const x = t * GRAPH_WIDTH;
    const y =
      (mode === 'linear' ? progressLinear(t) : progressEaseIn(t)) *
      GRAPH_HEIGHT;
    return [x, y];
  });
}

function calculatePath(mode: TimingMode): string {
  const points = getPoints(mode);
  const parts = points.map(
    ([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`
  );
  return parts.join(' ');
}

function StepsTimingGraph() {
  const [mode, setMode] = React.useState<TimingMode>('steps');

  const prefersReducedMotion = usePrefersReducedMotion();

  const path = calculatePath(mode);
  const spring = useSpring({
    d: path,
    immediate: prefersReducedMotion,
  });

  return (
    <Wrapper>
      <StyledToggleButtonGroup
        title="Timing function:"
        options={[
          { label: 'linear', value: 'linear' },
          { label: 'ease-in', value: 'ease-in' },
          { label: 'steps(5)', value: 'steps' },
        ]}
        value={mode}
        handleSelect={(value) => {
          setMode(value as TimingMode);
        }}
      />
      <GraphOuterWrapper>
        <LeftLabel>
          <Rotated>—— Progression ——</Rotated>
        </LeftLabel>
        <GraphBorder>
          <GraphBG>
            <Svg
              viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
              preserveAspectRatio="none"
            >
              <BGLine d={spring.d} fill="none" />
              <Line d={spring.d} fill="none" />
            </Svg>
          </GraphBG>
        </GraphBorder>
        <Spacer />
      </GraphOuterWrapper>
      <BottomLabel>
        <Shifted>—— Time ——</Shifted>
      </BottomLabel>
    </Wrapper>
  );
}

const Wrapper = styled(Space)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 1rem;
  margin-bottom: 4rem;
  padding: 1rem;
  padding-bottom: 2rem;
  border-radius: 16px;

  html[data-color-mode='dark'] & {
    outline: var(--page-border);
    outline-offset: -1px;
  }

  @media (max-width: 26rem) {
    margin-inline: -1rem;
  }
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  gap: 0px;
  margin-bottom: 1.5rem;
`;

const GraphOuterWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  font-family: var(--font-family-mono);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const LeftLabel = styled(Label)`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 2rem;
  white-space: nowrap;
`;

const BottomLabel = styled(Label)`
  margin-top: -9px;
`;

const Spacer = styled(LeftLabel)`
  @media (max-width: 26rem) {
    display: none;
  }
`;

const Rotated = styled.div`
  transform: rotate(-90deg);
`;
const Shifted = styled.div`
  @media (max-width: 26rem) {
    transform: translateX(1rem);
  }
`;

// I need to separate the Border/BG elements because the BG element uses a gradient to create the horizontal lines, and there needs to be a gap between those lines and the outer border.
const GraphBorder = styled.div`
  --inner-space: 4px;
  flex: 1;
  max-width: 500px;
  border-left: 2px solid var(--color-text);
  border-bottom: 2px solid var(--color-text);
  border-right: 1px solid var(--color-gray-300);
  border-radius: 2px 0 2px 2px;
  padding-left: var(--inner-space);
  padding-right: var(--inner-space);
  padding-bottom: var(--inner-space);
`;

const GraphBG = styled.div`
  --row-height: 50px;
  display: flex;
  height: calc(var(--row-height) * 4 + 4px);
  position: relative;
  background: repeating-linear-gradient(
    to top,
    transparent 0,
    transparent var(--row-height),
    var(--color-gray-300) var(--row-height)
      calc(var(--row-height) + 1px)
  );
`;

const Svg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: scaleY(-1);
  overflow: visible;
`;

const Line = styled(animated.path)`
  stroke: ${DEMO_COLORS_DARK.primary500};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
`;

const BGLine = styled(Line)`
  stroke: var(--color-background);
  stroke-width: 7px;
`;

export default StepsTimingGraph;
